import { ACTIONS } from '@pages/Management/ConversationManagement/action';
import { useForm } from '@pages/Management/hooks/use-editable-form';
import { isResponse } from '@services/fetcher';
import { Alert, Button, Form, Input, Modal } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

interface Values extends Record<string, unknown> {
  id: number;
  username: string;
}

interface OwnProps {
  onTransfer: () => void;
  onCancel: () => void;
  conversationId?: number;
}

const TransferOwnershipForm: FC<OwnProps> = ({ onTransfer, onCancel, conversationId }) => {
  const {
    form,
    isLoadingOrSubmitting,
    submitForm,
    error: formError,
    resetFetcher,
    resetError,
  } = useForm<Values, Values>('conversation-transfer-form', true, ({ response }) => {
    if (!response || isResponse(response)) {
      return;
    }

    onTransfer();
  });
  const onTriggerCancel = useCallback(() => {
    form.resetFields();
    resetFetcher();
    resetError();
    onCancel();
  }, [form, resetFetcher, resetError, onCancel]);

  useEffect(() => {
    if (conversationId) {
      form.setFieldValue('id', conversationId);
    }
  }, [conversationId, form]);

  return (
    <Modal
      open={conversationId !== undefined}
      title="Transfer to Another Organization"
      onCancel={onTriggerCancel}
      footer={[
        <Button key="cancel" disabled={isLoadingOrSubmitting} onClick={onTriggerCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoadingOrSubmitting} onClick={form.submit}>
          Ok
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        disabled={isLoadingOrSubmitting}
        onFinish={() => submitForm(ACTIONS.Transfer, 'POST')}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            form.submit();
          }
        }}
        initialValues={{ id: conversationId }}
      >
        {formError && <Alert message={formError} style={{ margin: '24px 0' }} type="error" />}
        <Form.Item hidden name="id">
          <Input hidden />
        </Form.Item>
        <Form.Item
          name="username"
          label="Administrator username"
          rules={[{ required: true, message: 'Username is required' }]}
          tooltip="Enter the username of an administrator from the target organization."
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TransferOwnershipForm;
